import {UI} from "../../../../stem-core/src/ui/UIBase";

import {BasePanelPage, BasePanelPageStyle} from "./BasePanelPage";
import {iFrameState} from "../../../services/IFrameState";
import {authService} from "../../../../client/connection/Services";
import {PoweredByBlink} from "../../PoweredByBlink";
import {isSmallScreen} from "../../../Utils";
import {GATE_TYPE, PANEL_FRAME} from "../../../../blink-sdk/Constants";
import {styleRule, styleRuleInherit} from "../../../../stem-core/src/decorators/Style";
import {registerStyle} from "../../../../stem-core/src/ui/style/Theme";
import {PanelLoginFooterBuilder, PromptInlineUserDashboard} from "../../panel-elements/LoginFooter";
import {PanelImage, PanelLink, PanelText} from "../../panel-elements/Primitives";
import {PanelActionButtonBuilder} from "../../panel-elements/ActionButton";
import {AuthService} from "../../../../client/connection/services/AuthService";
import {SubscribeFlowController} from "../../flows/SubscribeFlowController";
import {DonateFlowController} from "../../flows/DonateFlowController";
import {PurchaseFlowController} from "../../flows/PurchaseFlowController";
import {merchantStyleRule} from "../../Security";
import {CloseController, LinkController, RefreshController} from "../../flows/BasicFlowControllers";
import {IdentityFlowController} from "../../flows/IdentityFlowController";
import {StyleElement} from "../../../../stem-core/src/ui/StyleElement";
import {NewsletterFlowController} from "../../flows/NewsletterFlowController";
import {PaywallCloseButton} from "../../PaywallCloseButton";


export class BasePanelStyle extends BasePanelPageStyle {
    // TODO: This should not be in the style sheet
    shouldDisplayFrame = () => iFrameState.gateType !== GATE_TYPE.banner && iFrameState.frame === PANEL_FRAME.logo && !iFrameState.panelContent;

    @styleRuleInherit
    paywallPage = {
        textAlign: "center",
        opacity: 1,
        transition: "opacity ease-in .15s",
        maxWidth: "100%",
        display: () => iFrameState.gateType === GATE_TYPE.banner ? "flex" : "",
        flexDirection: "row",
        margin: "auto",
        ...(iFrameState.panelContent ? {
            overflow: ["overlay", "auto"],
            background: "",
            display: "block",
            borderRadius: 0,
            padding: 0,
            border: "",
        } : {})
    };

    @styleRule
    message = this.themeProps.PAYWALL_MESSAGE_STYLE;

    @styleRule
    paragraph = {};

    @styleRuleInherit
    button = {
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_REGULAR + "!important",
    };

    @styleRule
    bannerLeft = {
        flex: 1,
    };

    @styleRule
    bannerRight = {
        width: 200,
    };

    @styleRule
    verticalSeparator = {
        width: 1,
        alignSelf: "stretch",
        background: this.themeProps.MERCHANT_FRAME_BORDER_COLOR,
        margin: "0 30px",
    };

    @styleRule
    logo = {
        padding: "0",
        transform: "translateY(-2px)",
        marginLeft: 2,
    };

    @merchantStyleRule
    pgLogoStyle = {
        ...iFrameState.logoStyle,
    };
}

export const panelClassMap = new Map([
    ...[
        "div", "span", "p", "i", "nav",
        "strong", "em", "br", "b", "hr",
        "ul", "ol", "li", "code", "var",
        "h1", "h2", "h3", "h4", "h5", "h6",
    ].map(tag => ([tag, UI.Primitive(tag)])),
    ["If", UI.Primitive("div")],
    ["Style", StyleElement],
    ["PanelLink", PanelLink], // Duplicate because of our current rendering engine (the DOM)
    ["Link", PanelLink],
    ["PanelImage", PanelImage],
    ["Text", PanelText],
    ["Close", PaywallCloseButton],
    ["SubscribeButton", PanelActionButtonBuilder(SubscribeFlowController)],
    ["DonateButton", PanelActionButtonBuilder(DonateFlowController)],
    ["NewsletterButton", PanelActionButtonBuilder(NewsletterFlowController)],
    ["LinkButton", PanelActionButtonBuilder(LinkController)],
    ["CloseButton", PanelActionButtonBuilder(CloseController)],
    ["IdentityButton", PanelActionButtonBuilder(IdentityFlowController)],
    ["RefreshButton", PanelActionButtonBuilder(RefreshController)],
    ["SubscribeLoginFooter", PanelLoginFooterBuilder(SubscribeFlowController)],
    ["DonateLoginFooter", PanelLoginFooterBuilder(DonateFlowController)],
    ["PurchaseLoginFooter", PanelLoginFooterBuilder(PurchaseFlowController)],
    ["PromptInlineUserDashboard", PromptInlineUserDashboard],
    ["PoweredByBlink", PoweredByBlink],
]);


function convertToUI(value, inRecursion = false) {
    if (typeof value === "string") {
        return inRecursion ? new UI.TextElement(value) : <PanelText>{value}</PanelText>;
    }
    if (!value) {
        return null;
    }
    if (Array.isArray(value)) {
        return value.map(x => convertToUI(x, inRecursion));
    }
    const children = (value.children && convertToUI(value.children, true)) || [];
    const classObject = panelClassMap.get(value.tag);
    if (!classObject) {
        return null;
    }
    if (classObject.prototype instanceof UI.Element) {
        return UI.createElement(classObject, {...value}, ...children);
    }
    return classObject({...value}, children);
}

@registerStyle(BasePanelStyle)
export class BasePanel extends BasePanelPage {
    widthValue = iFrameState.panelContent ? "" : 660;

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        if ((isSmallScreen() || iFrameState.gateType !== GATE_TYPE.popup) && !iFrameState.panelContent) {
            attr.setStyle("maxWidth", "100%");
        }
        if (isSmallScreen() && iFrameState.panelContent && iFrameState.gateType === GATE_TYPE.popup) {
            attr.setStyle("width", this.getMaxWidth());
        } else if (this.widthValue && iFrameState.panelContent) {
            attr.setStyle("width", "");
        }
        if (this.heightValue && iFrameState.panelContent) {
            attr.setStyle("height", "");
        }
    }

    getControllerClass() {
        return null;
    }

    renderCustomContent(content) {
        return [
            convertToUI(content),
        ];
    }

    renderDefaultFooter() {
        return [
            PanelLoginFooterBuilder(this.getControllerClass())(),
            <PoweredByBlink/>,
        ];
    }

    renderDefault() {
        const {styleSheet} = this;
        const {logoSrc, logoAlt} = iFrameState;
        const logo = logoSrc && <PanelImage src={logoSrc} alt={logoAlt} className={styleSheet.pgLogoStyle}/>;
        const controllerClass = this.getControllerClass();
        const topSection = [
            logo,
            <PanelText>{(new controllerClass()).getHeaderDefaultText()}</PanelText>,
        ];
        const bottomSection = [
            PanelActionButtonBuilder(controllerClass)({
                target: "_blank", // This button is a link for browser popup blocking issues.
            }),
            this.renderDefaultFooter(),
        ];

        if (iFrameState.gateType === GATE_TYPE.banner) {
            return [
                <div className={styleSheet.bannerLeft}>{topSection}</div>,
                <div className={styleSheet.verticalSeparator}/>,
                <div className={styleSheet.bannerRight}>{bottomSection}</div>,
            ];
        }
        return [topSection, bottomSection];
    }

    render() {
        const {panelContent} = iFrameState;
        return panelContent ? this.renderCustomContent(panelContent) : this.renderDefault();
    }

    onMount() {
        super.onMount();
        this.attachListener(authService, AuthService.EventTypes.AUTHENTICATION_CHANGE, () => this.redraw());
    }
}
